import { z } from 'zod';
import { APP_ROLES, MODULES } from '../enums';
import { FlagsSchema, Id, PermissionMatrixSchema, Phone, TenantTypeSchema } from './common';

export const ClientApp = z.enum(['passenger', 'driver', 'console', 'admin']);
export type ClientApp = z.infer<typeof ClientApp>;

export const OtpRequestBody = z.object({ phone: Phone, app: ClientApp });
export const OtpRequestResponse = z.object({
  ok: z.literal(true),
  expiresInS: z.number().int(),
  resendInS: z.number().int(),
});

export const OtpVerifyBody = z.object({
  phone: Phone,
  code: z.string().regex(/^\d{6}$/, 'Kod 6 haneli olmalı'),
  app: ClientApp,
  device: z.string().max(120).optional(),
});

export const UserSchema = z.object({
  id: Id,
  phone: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});
export type User = z.infer<typeof UserSchema>;

export const TokenPair = z.object({ accessToken: z.string(), refreshToken: z.string(), expiresInS: z.number().int() });
export type TokenPair = z.infer<typeof TokenPair>;

export const OtpVerifyResponse = TokenPair.extend({ user: UserSchema, isNew: z.boolean() });
export type OtpVerifyResponse = z.infer<typeof OtpVerifyResponse>;

export const RefreshBody = z.object({ refreshToken: z.string().min(20) });

export const MembershipSchema = z.object({
  id: Id,
  companyId: Id,
  companyName: z.string(),
  tenantType: TenantTypeSchema,
  institutionKind: z.string().nullable(),
  roleKey: z.string(),
  roleName: z.string(),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const WhiteLabelSchema = z
  .object({
    appName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    brand: z.string(),
    brandStrong: z.string(),
    brandInk: z.string(),
    brandTint: z.string(),
    onBrand: z.string(),
  })
  .nullable();

export const MeContext = z.object({
  user: UserSchema,
  memberships: z.array(MembershipSchema),
  active: MembershipSchema.nullable(),
  permissions: PermissionMatrixSchema,
  flags: FlagsSchema,
  appRoles: z.array(z.enum(APP_ROLES)),
  /** P08 / D04: kullanıcının seçtiği kullanım (SELF | CHILD | BOTH | DRIVER | GUIDE) */
  appRole: z.string().nullable(),
  whiteLabel: WhiteLabelSchema,
  impersonation: z.object({ adminName: z.string(), expiresAt: z.string(), reason: z.string() }).nullable(),
});
export type MeContext = z.infer<typeof MeContext>;

export const UpdateMeBody = z.object({
  firstName: z.string().trim().min(1, 'Ad gerekli').max(60).optional(),
  lastName: z.string().trim().min(1, 'Soyad gerekli').max(60).optional(),
  email: z.email('Geçerli bir e-posta girin').optional().or(z.literal('')),
  /** /v1/uploads (AVATAR) ile yüklenen dosya; null fotoğrafı kaldırır */
  avatarFileId: z.string().nullable().optional(),
});

export const AppRoleBody = z.object({ role: z.enum(['SELF', 'CHILD', 'BOTH', 'DRIVER', 'GUIDE']) });

export const ModuleSchema = z.enum(MODULES);
